import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const BOOK_BEAUTY_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/psnsg0g1_FAVICON%20AH.png";

const hairSubLinks = [
  { name: 'Hair Services', path: '/hair' },
  { name: 'Blondes & Colour', path: '/blonde-specialist' },
  { name: 'Hair Extensions', path: '/hair-extensions' },
];

const beautySubLinks = [
  { name: 'Beauty Services', path: '/beauty' },
  { name: 'Beauty Price List', path: '/beauty-price-list' },
  { name: 'HIFU', path: '/hifu' },
  { name: 'Super Hair Removal', path: '/super-hair-removal' },
  { name: 'Facials', path: '/facials' },
];

const wellnessSubLinks = [
  { name: 'Waterfall Head Spa', path: '/waterfall-head-spa' },
];

const aboutSubLinks = [
  { name: 'Sustainability', path: '/sustainability' },
  { name: 'La Biosthetique', path: '/our-products' },
  { name: 'Aspect Skincare', path: '/aspect-skincare' },
];

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Hair', path: '/hair', children: hairSubLinks },
  { name: 'Beauty', path: '/beauty', children: beautySubLinks },
  { name: 'Waterfall Head Spa', path: '/waterfall-head-spa' },
  { name: 'About', path: '/sustainability', children: aboutSubLinks },
  { name: 'Contact', path: '/contact' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenSections, setMobileOpenSections] = useState({});
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileOpenSections({});
  }, [location]);

  const handleMouseEnter = (name) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const toggleMobileSection = (name) => {
    setMobileOpenSections((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const isChildActive = (children) => children?.some((sub) => location.pathname === sub.path);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#FFFDF9]/95 backdrop-blur-md border-b border-[#D4C4B0]/30 py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img
              src={LOGO_URL}
              alt="Artistry House"
              className="w-10 h-10 object-contain transition-transform group-hover:scale-110"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-medium text-[#2C2420] leading-tight">Artistry House</h1>
              <p className="text-xs text-[#6B5D4D] tracking-wide">Hair · Beauty · Wellness</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(link.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                    className={`nav-link text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                      isChildActive(link.children) ? 'text-[#8D7B68]' : 'text-[#3D352D] hover:text-[#8D7B68]'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                  </Link>

                  {openDropdown === link.name && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50" data-testid={`${link.name.toLowerCase()}-dropdown`}>
                      <div className="bg-[#FFFDF9] border border-[#D4C4B0]/50 rounded-lg shadow-lg py-2 min-w-[220px]">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`dropdown-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`block px-5 py-2.5 text-sm transition-colors ${
                              location.pathname === child.path
                                ? 'text-[#8D7B68] bg-[#8D7B68]/5'
                                : 'text-[#3D352D] hover:text-[#8D7B68] hover:bg-[#8D7B68]/5'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`nav-link text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'text-[#8D7B68]' : 'text-[#3D352D] hover:text-[#8D7B68]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA & Mobile Menu */}
          <div className="flex items-center gap-3">
            <div className="hidden xl:flex items-center gap-4 text-sm text-[#6B5D4D] mr-2">
              <a href="tel:0398826969" className="flex items-center gap-2 hover:text-[#8D7B68] transition-colors">
                <Phone className="w-4 h-4" />
                <span>(03) 9882 6969</span>
              </a>
            </div>

            <a href={BOOK_BEAUTY_URL} target="_blank" rel="noopener noreferrer" data-testid="book-beauty-btn">
              <Button className="hidden md:flex bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-4 py-2 text-sm font-medium transition-all hover:-translate-y-0.5">
                Book Beauty & Wellness
              </Button>
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="book-hair-btn">
              <Button variant="outline" className="hidden md:flex border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-4 py-2 text-sm font-medium">
                Book Hair
              </Button>
            </a>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#3D352D] hover:text-[#8D7B68] transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[73px] bg-[#FFFDF9] z-40 animate-fade-in overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col p-6 space-y-1">
            {navLinks.map((link, index) =>
              link.children ? (
                <div key={link.path} style={{ animationDelay: `${index * 50}ms` }} className="animate-fade-in-up">
                  <button
                    onClick={() => toggleMobileSection(link.name)}
                    className={`w-full flex items-center justify-between py-4 px-4 text-lg font-medium border-b border-[#D4C4B0] transition-all ${
                      isChildActive(link.children) ? 'text-[#8D7B68] bg-[#8D7B68]/5' : 'text-[#3D352D] hover:text-[#8D7B68] hover:bg-[#8D7B68]/5'
                    }`}
                    data-testid={`mobile-${link.name.toLowerCase()}-toggle`}
                  >
                    <span>{link.name}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${mobileOpenSections[link.name] ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileOpenSections[link.name] && (
                    <div className="pl-4 bg-[#F5F0EB]/50">
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          data-testid={`mobile-dropdown-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`block py-3 px-4 text-base border-b border-[#D4C4B0]/50 transition-all ${
                            location.pathname === child.path ? 'text-[#8D7B68] bg-[#8D7B68]/5' : 'text-[#5D4A3A] hover:text-[#8D7B68]'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`py-4 px-4 text-lg font-medium border-b border-[#D4C4B0] transition-all animate-fade-in-up ${
                    location.pathname === link.path ? 'text-[#8D7B68] bg-[#8D7B68]/5' : 'text-[#3D352D] hover:text-[#8D7B68] hover:bg-[#8D7B68]/5'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {link.name}
                </Link>
              )
            )}

            <div className="pt-6 space-y-4">
              <a href="tel:0398826969" className="flex items-center gap-3 text-[#6B5D4D] hover:text-[#8D7B68] transition-colors">
                <Phone className="w-5 h-5" />
                <span>(03) 9882 6969</span>
              </a>
              <div className="flex items-start gap-3 text-[#6B5D4D]">
                <MapPin className="w-5 h-5 mt-0.5" />
                <span>925 Burke Rd, Camberwell VIC 3124</span>
              </div>
            </div>

            <div className="pt-6 space-y-3">
              <a href={BOOK_BEAUTY_URL} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full py-4 text-base font-medium">
                  Book Beauty & Wellness
                </Button>
              </a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-testid="mobile-book-btn">
                <Button variant="outline" className="w-full border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full py-4 text-base font-medium">
                  Book Hair
                </Button>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
